/**
 * Financial-analysis context carried between the Historical Financial
 * Analysis and Solvency Analysis destinations (MOR-1280) — parsing it from
 * the route's search params, and resolving an indicator's evidence period
 * against the extracted periods.
 */

import {
  GRANULARITIES,
  displayPeriodLabel,
  type Granularity,
} from "./granularity";
import type { FinancialAnalysisContext } from "./types";

/** Type guard for a granularity key from untrusted search params. */
function isGranularity(value: unknown): value is Granularity {
  return GRANULARITIES.some((g) => g.key === value);
}

/** Returns `value` only when it is a non-blank string. */
function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/** Validates untrusted search params into a context; unknown values drop. */
export function parseFinancialContext(
  search: Record<string, unknown>,
): FinancialAnalysisContext {
  return {
    granularity: isGranularity(search.granularity)
      ? search.granularity
      : undefined,
    period: nonEmptyString(search.period),
    indicator: nonEmptyString(search.indicator),
  };
}

interface PeriodLike {
  _id: string;
  periodLabel: string;
  periodType: Granularity;
  status?: string;
  endDate?: string;
  [field: string]: unknown;
}

/**
 * The active period an evidence label refers to — matched on the stored
 * label or its display form ("As at …"). Null when the label names a period
 * that was not extracted, so the caller can say so instead of silently
 * showing a different one.
 */
export function findEvidencePeriod<P extends PeriodLike>(
  periods: ReadonlyArray<P>,
  label: string | undefined,
): P | null {
  if (!label) return null;
  const wanted = label.trim().toLowerCase();
  return (
    periods.find(
      (p) =>
        p.status === "active" &&
        (p.periodLabel.trim().toLowerCase() === wanted ||
          displayPeriodLabel(p).trim().toLowerCase() === wanted),
    ) ?? null
  );
}

/**
 * Granularity the worksheet opens on: an explicit choice carried in the
 * route wins (if it has periods), then the evidence period's own type, then
 * the coarsest granularity with data.
 */
export function resolveInitialGranularity(
  counts: Record<Granularity, number>,
  preferred: Granularity | undefined,
  evidencePeriodType: Granularity | undefined,
  fallback: Granularity,
): Granularity {
  if (preferred && counts[preferred] > 0) return preferred;
  if (evidencePeriodType && counts[evidencePeriodType] > 0) {
    return evidencePeriodType;
  }
  return fallback;
}

/**
 * Evidence period an indicator points supporting financials at: of the
 * periods its supporting values cite, the latest one that was extracted
 * (by end date); failing any match, the first cited label, so the worksheet
 * can say that period is unavailable. None when no value cites a period.
 */
export function evidencePeriodLabel<P extends PeriodLike>(
  supportingValues: ReadonlyArray<{ periodLabel: string }>,
  periods: ReadonlyArray<P>,
): string | undefined {
  const cited = supportingValues
    .map((v) => v.periodLabel.trim())
    .filter((l) => l !== "");
  const latestMatched = cited
    .map((label) => ({ label, period: findEvidencePeriod(periods, label) }))
    .filter((m): m is { label: string; period: P } => m.period !== null)
    .sort((a, b) =>
      (a.period.endDate ?? a.period.periodLabel).localeCompare(
        b.period.endDate ?? b.period.periodLabel,
      ),
    )
    .slice(-1)[0];
  return latestMatched?.label ?? cited[0];
}
